import useHttpProvider from "@/common/hooks/use-http";
import { ResponseError } from "@/common/models/error";
import { AuthenticationProvider } from "@/common/providers/authentication";
import { notifyError, notifySuccess } from "@/components/ui/sonner";
import * as AuthenticationServices from "@/features/authentication/services/authentication";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

const passwordPattern = /^(?=.*\d).{8,}$/;

const FormSchema = z
	.object({
		password: z
			.string({ message: "Password is required" })
			.min(8, "Password must be at least 8 characters long"),
		newPassword: z
			.string({ message: "New Password is required" })
			.min(8, "New Password must be at least 8 characters long")
			.regex(passwordPattern, "New Password must contain at least one number"),
		confirmPassword: z
			.string({ message: "Confirm Password is required" })
			.min(8, "Confirm Password must be at least 8 characters long")
			.regex(passwordPattern, "Password must contain at least one number"),
	})
	.superRefine((data, ctx) => {
		if (data.newPassword !== data.confirmPassword) {
			ctx.addIssue({
				code: "custom",
				message: "Passwords do not match",
				path: ["confirmPassword"],
			});
		}

		if (data.password === data.newPassword) {
			ctx.addIssue({
				code: "custom",
				message: "New password cannot be the same as the current password",
				path: ["newPassword"],
			});
		}
	});

function useChangePassword() {
	const { http } = useHttpProvider();

	const form = useForm({
		resolver: zodResolver(FormSchema),
		defaultValues: {},
	});

	async function onSubmit(data: z.infer<typeof FormSchema>) {
		try {
			const response = await AuthenticationServices.changePassword(
				http,
				data.password,
				data.newPassword
			);

			await AuthenticationProvider.signin(response);

			form.reset({
				password: "",
				newPassword: "",
				confirmPassword: "",
			});

			notifySuccess("Authentication", "Password changed successfully!");
		} catch (error) {
			const detail = (error as ResponseError).detail;

			if (detail === "invalid_password") {
				notifyError("Invalid password", "Please enter a valid password");
				form.setError("password", {
					type: "manual",
					message: "Invalid password",
				});
				return;
			}
		}
	}

	return {
		form,
		onSubmit,
	};
}

export default useChangePassword;
